import { useEffect, useRef, useState } from 'react'
import { Box, Button, Group, Select, Stack, Text, TextInput } from '@grovus/design-system'
import { createInvite, type Invite } from '../data/members'
import { MEMBERS_COPY, type Lang } from '../i18n'
import { fmt } from '../lib/format'
import { invitableRoles, type Role } from '../lib/roles'

const BORDER = '1px solid var(--mantine-color-default-border)'
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

interface FormProps {
  lang: Lang
  companyId: string
  /** The inviter's role: it limits the Role options (`invitableRoles`). */
  actor: Role
  onCreated: (invite: Invite, token: string) => void
  /**
   * `409 already_invited`: refetches the pending list and resolves the row by email,
   * case-insensitively, so New link resends the invitation the server holds now.
   */
  onAlreadyInvited: (email: string) => Promise<Invite | null>
  onResend: (invite: Invite) => void
  /** The create may have happened: the page refetches the pending list (plan, 15.3). */
  onUncertain: (email: string) => void
  onForbidden: () => void
  onNotFound: () => void
  onCancel: () => void
}

type Line = { text: string; resend?: Invite } | null

/**
 * The invite form (GRO-106 design, screen 1): Email, Role (default Member), the Admin note,
 * Invite and Cancel. A refusal stays in the form as one line; every other outcome is the
 * page's to show.
 */
export function InviteForm({ lang, companyId, actor, onCreated, onAlreadyInvited, onResend, onUncertain, onForbidden, onNotFound, onCancel }: FormProps) {
  const copy = MEMBERS_COPY[lang]
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<Role>('member')
  const [line, setLine] = useState<Line>(null)
  const [busy, setBusy] = useState(false)
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => { alive.current = false }
  }, [])

  async function submit(event: { preventDefault: () => void }) {
    event.preventDefault()
    if (busy) return
    const address = email.trim()
    if (!EMAIL.test(address)) {
      setLine({ text: copy.invalidEmail })
      return
    }
    setBusy(true)
    setLine(null)
    const result = await createInvite(companyId, address, role)
    if (!alive.current) return
    setBusy(false)
    switch (result.status) {
      case 'ok':
        onCreated(result.body.invite, result.body.token)
        return
      case 'stale':
      case 'expired':
        return
      case 'forbidden':
        setLine({ text: copy.forbidden })
        onForbidden()
        return
      case 'notFound':
        onNotFound()
        return
      case 'unreachable':
        onUncertain(address)
        return
      case 'unavailable':
        setLine({ text: copy.failed })
        return
      case 'refused':
        break
    }
    switch (result.code) {
      case 'invalid_email':
        setLine({ text: copy.invalidEmail })
        return
      case 'already_member':
        setLine({ text: fmt(copy.alreadyMember, { email: address }) })
        return
      case 'invites_not_open':
        setLine({ text: copy.notOpen })
        return
      case 'already_invited': {
        setBusy(true)
        const row = await onAlreadyInvited(address)
        if (!alive.current) return
        setBusy(false)
        setLine(row === null ? { text: copy.failed } : { text: fmt(copy.alreadyInvited, { email: address }), resend: row })
        return
      }
      default:
        setLine({ text: copy.failed })
    }
  }

  const roles = invitableRoles(actor)
  return (
    <Box p={16} style={{ border: BORDER, borderRadius: 'var(--mantine-radius-lg)' }} data-testid="invite-form">
      <form onSubmit={(event) => { void submit(event) }} noValidate>
        <Stack gap={12}>
          <Group align="flex-end" gap="12px 16px" wrap="wrap">
            <TextInput
              label={copy.email}
              type="email"
              value={email}
              onChange={(event) => setEmail(event.currentTarget.value)}
              autoComplete="off"
              style={{ flex: '1 1 240px' }}
            />
            <Select
              label={copy.role}
              data={roles.map((value) => ({ value, label: copy.roles[value] }))}
              value={role}
              onChange={(value) => value !== null && setRole(value as Role)}
              allowDeselect={false}
              w={{ base: '100%', sm: 180 }}
            />
          </Group>
          {actor === 'admin' && <Text fz="sm" className="grv-muted">{copy.adminHint}</Text>}
          {line !== null && (
            <Group gap="xs" role="alert">
              <Text fz="sm">{line.text}</Text>
              {line.resend && (
                <Button size="compact-sm" variant="default" onClick={() => onResend(line.resend!)}>{copy.newLink}</Button>
              )}
            </Group>
          )}
          <Group gap="xs">
            <Button type="submit" disabled={busy}>{copy.send}</Button>
            <Button variant="default" onClick={onCancel}>{copy.cancel}</Button>
          </Group>
        </Stack>
      </form>
    </Box>
  )
}

interface ResultProps {
  lang: Lang
  email: string
  role: Role
  token: string
  onDone: () => void
}

/**
 * The result block after a create or a New link: the invitation link, Copy link and Done.
 * The token lives only here; Done drops it, and nothing stores it.
 */
export function InviteResult({ lang, email, role, token, onDone }: ResultProps) {
  const copy = MEMBERS_COPY[lang]
  const link = `${window.location.origin}/invite#${token}`
  const [copied, setCopied] = useState(false)
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => { alive.current = false }
  }, [])

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(link)
      if (alive.current) setCopied(true)
    } catch {
      if (alive.current) setCopied(false)
    }
  }

  return (
    <Stack gap={12} p={16} style={{ border: BORDER, borderRadius: 'var(--mantine-radius-lg)' }} data-testid="invite-result">
      <Text fw={500}>{fmt(copy.created, { email, role: copy.roles[role] })}</Text>
      <Group align="flex-end" gap="12px 16px" wrap="wrap">
        <TextInput label={copy.linkLabel} value={link} readOnly style={{ flex: '1 1 280px' }} />
        <Button variant="default" onClick={() => { void copyLink() }}>{copy.copyLink}</Button>
      </Group>
      {copied && <Text fz="sm" role="status">{copy.linkCopied}</Text>}
      <Text fz="sm" className="grv-muted">{copy.linkNote}</Text>
      <Group><Button onClick={onDone}>{copy.done}</Button></Group>
    </Stack>
  )
}
